import { Capacitor } from '@capacitor/core';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import {
    Alert,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MessageKey, useT } from '../../i18n';
import { config } from '../../monetization/config';
import { RootState } from '../../store';
import { ALARM_SOUNDS, AlarmSoundId, playAlarmSound } from '../alarmSounds';
import { sound } from '../audio';
import { playCue } from '../feedback';
import { MusicListener } from '../music';
import {
    AlarmProblem,
    ensureFullScreenAllowed,
    getAlarmProblem,
    openAlarmSettings,
} from '../nativeAlarm';
import { Durations } from '../timer';
import {
    setAlarmSound,
    setDuration,
    setLockScreenAlarm,
    setMuted,
    setStreakReminders,
} from '../tomatoSlice';

interface Props {
    open: boolean;
    onClose: () => void;
    /** Party mode: the tomato listens through the microphone and dances to the beat. */
    listening: boolean;
    listenFailed: boolean;
    onToggleListening: () => void;
    /** Developer option; shown only when `config.devTools` is on. */
    onSkipToLast: () => void;
}

/**
 * A minutes field that keeps what is being typed as text, so clearing it and typing a new number does
 * not fight the stored (clamped) value. It commits valid numbers as they are typed and snaps back to
 * the stored value on blur.
 */
const MinutesField: React.FC<{ field: keyof Durations }> = ({ field }) => {
    const t = useT();
    const dispatch = useDispatch();
    const stored = useSelector((s: RootState) => s.tomato.durations[field]);
    const [draft, setDraft] = useState(String(stored));
    useEffect(() => {
        // Follow the store when it changes elsewhere (e.g. the roulette limits pushing each other).
        setDraft((d) => (Number(d) === stored ? d : String(stored)));
    }, [stored]);

    return (
        <TextField
            label={t(`drawer.minutes.${field}` as MessageKey)}
            type="number"
            value={draft}
            onChange={(e) => {
                const text = e.target.value;
                setDraft(text);
                const n = Number(text);
                if (text !== '' && Number.isFinite(n) && n >= 1) {
                    dispatch(setDuration({ key: field, minutes: n }));
                }
            }}
            onBlur={() => setDraft(String(stored))}
            inputProps={{ min: 1, max: 180, inputMode: 'numeric' }}
        />
    );
};

/**
 * Phone app: says why the alarm may not light the screen (notifications off, the ring channel turned down,
 * Android 14+ full-screen notifications off) with a button to the system page that fixes it. Checked when the
 * dialog opens and again when the person returns from the system settings.
 */
const AlarmReadiness: React.FC<{ open: boolean; wantsFullScreen: boolean }> = ({
    open,
    wantsFullScreen,
}) => {
    const t = useT();
    const [problem, setProblem] = useState<AlarmProblem | null>(null);
    useEffect(() => {
        if (!open) return;
        let alive = true;
        const check = () =>
            void getAlarmProblem().then((p) => alive && setProblem(p));
        check();
        document.addEventListener('visibilitychange', check);
        return () => {
            alive = false;
            document.removeEventListener('visibilitychange', check);
        };
    }, [open]);
    if (!problem || (problem === 'fullScreen' && !wantsFullScreen)) return null;
    return (
        <Alert
            severity="warning"
            sx={{ mt: 1.5 }}
            action={
                <Button
                    color="inherit"
                    size="small"
                    onClick={() => void openAlarmSettings(problem)}
                >
                    {t('drawer.alarmFix')}
                </Button>
            }
        >
            {t(`drawer.alarmProblem.${problem}` as MessageKey)}
        </Alert>
    );
};

/** Browser: explains a blocked notification permission, or offers to ask for it. */
const WebNotifyHint: React.FC = () => {
    const t = useT();
    const supported = typeof Notification !== 'undefined';
    const [permission, setPermission] = useState<NotificationPermission>(
        supported ? Notification.permission : 'denied'
    );
    if (!supported || permission === 'granted') return null;
    return (
        <Alert
            severity={permission === 'denied' ? 'warning' : 'info'}
            sx={{ mt: 1.5 }}
            action={
                permission === 'default' ? (
                    <Button
                        color="inherit"
                        size="small"
                        onClick={() =>
                            void Notification.requestPermission().then(
                                setPermission
                            )
                        }
                    >
                        {t('drawer.webNotify.enable')}
                    </Button>
                ) : undefined
            }
        >
            {t(
                permission === 'denied'
                    ? 'drawer.webNotify.denied'
                    : 'drawer.webNotify.ask'
            )}
        </Alert>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
    title,
    children,
}) => (
    <Box sx={{ mb: 2.5 }}>
        <Typography
            variant="subtitle2"
            sx={{ mb: 1, fontWeight: 800, opacity: 0.8 }}
        >
            {title}
        </Typography>
        {children}
    </Box>
);

export const SettingsDialog: React.FC<Props> = ({
    open,
    onClose,
    listening,
    listenFailed,
    onToggleListening,
    onSkipToLast,
}) => {
    const t = useT();
    const dispatch = useDispatch();
    const muted = useSelector((s: RootState) => s.tomato.muted);
    const alarmSound = useSelector((s: RootState) => s.tomato.alarmSound);
    const lockScreenAlarm = useSelector(
        (s: RootState) => s.tomato.lockScreenAlarm
    );
    const streakReminders = useSelector(
        (s: RootState) => s.tomato.streakReminders
    );
    const native = Capacitor.isNativePlatform();
    const alarmChoices: AlarmSoundId[] = native
        ? [...ALARM_SOUNDS, 'system']
        : [...ALARM_SOUNDS];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                <Box component="span" sx={{ flex: 1, fontWeight: 800 }}>
                    {t('app.settings')}
                </Box>
                <IconButton onClick={onClose} aria-label={t('drawer.close')}>
                    <CloseRoundedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Section title={t('drawer.minutes')}>
                    <Stack direction="row" spacing={1.5}>
                        <MinutesField field="focus" />
                        <MinutesField field="short" />
                        <MinutesField field="long" />
                    </Stack>
                </Section>
                <Section title={t('drawer.roulette')}>
                    <Stack direction="row" spacing={1.5}>
                        <MinutesField field="rouletteMin" />
                        <MinutesField field="rouletteMax" />
                    </Stack>
                </Section>
                <Section title={t('drawer.alarm')}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            select
                            fullWidth
                            size="small"
                            value={alarmSound}
                            onChange={(e) => {
                                const id = e.target.value as AlarmSoundId;
                                dispatch(setAlarmSound(id));
                                if (id !== 'system') playAlarmSound(id);
                            }}
                        >
                            {alarmChoices.map((id) => (
                                <MenuItem key={id} value={id}>
                                    {t(`drawer.alarm.${id}` as MessageKey)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <IconButton
                            aria-label={t('drawer.alarm.preview')}
                            disabled={alarmSound === 'system'}
                            onClick={() => playAlarmSound(alarmSound)}
                        >
                            <PlayArrowRoundedIcon />
                        </IconButton>
                    </Stack>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}
                    >
                        {t('drawer.alarm.hint')}
                    </Typography>
                    {native && (
                        <Box sx={{ mt: 1 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={lockScreenAlarm}
                                        onChange={(e) => {
                                            dispatch(
                                                setLockScreenAlarm(
                                                    e.target.checked
                                                )
                                            );
                                            if (e.target.checked)
                                                void ensureFullScreenAllowed(
                                                    true
                                                );
                                        }}
                                    />
                                }
                                label={t('drawer.lockScreen')}
                            />
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', ml: 6 }}
                            >
                                {t('drawer.lockScreenHint')}
                            </Typography>
                            <AlarmReadiness
                                open={open}
                                wantsFullScreen={lockScreenAlarm}
                            />
                        </Box>
                    )}
                    {!native && <WebNotifyHint />}
                </Section>
                {native && (
                    <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={streakReminders}
                                    onChange={(e) =>
                                        dispatch(
                                            setStreakReminders(e.target.checked)
                                        )
                                    }
                                />
                            }
                            label={t('drawer.streakReminders')}
                        />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', ml: 6 }}
                        >
                            {t('drawer.streakRemindersHint')}
                        </Typography>
                    </Box>
                )}
                {MusicListener.supported() && (
                    <Box sx={{ mb: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={listening}
                                    onChange={onToggleListening}
                                />
                            }
                            label={t('drawer.party')}
                        />
                        <Typography
                            variant="caption"
                            color={listenFailed ? 'error' : 'text.secondary'}
                            sx={{ display: 'block', ml: 6 }}
                        >
                            {listenFailed
                                ? t('drawer.partyFailed')
                                : t('drawer.partyHint')}
                        </Typography>
                    </Box>
                )}
                <FormControlLabel
                    control={
                        <Switch
                            checked={!muted}
                            onChange={(e) => {
                                sound.muted = !e.target.checked;
                                dispatch(setMuted(!e.target.checked));
                                if (e.target.checked) playCue('ui');
                            }}
                        />
                    }
                    label={t('drawer.sound')}
                />
                {config.devTools && (
                    <Section title={t('drawer.developer')}>
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={onSkipToLast}
                        >
                            {t('drawer.skipLast')}
                        </Button>
                    </Section>
                )}
            </DialogContent>
        </Dialog>
    );
};
